import React from "react";
import { AlertTriangle, Eye, Info, CheckCircle2 } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";

/**
 * Signals, translated for the person reading them.
 *
 * The server emits deterministic `signals[]` — a `kind`, a label, a detail
 * sentence and the evidence (metric, value, threshold). That is a correct
 * measurement feed and a poor management brief: every row restates an arithmetic
 * fact ("Idle is 33% of tracked time") and leaves the reader to work out whether
 * it is a problem, a nothing, or good news.
 *
 * This module adds the one thing the server cannot: what it MEANS for a manager,
 * and whether it is worth acting on. Every framing here is tied to a `kind`, so
 * it is deterministic and reviewable — no model, no guessing per person.
 *
 * Two bugs this replaces:
 *   · positive signals rendered in the same amber as problems, because the old
 *     inline map sent both `medium` and `positive` to "warning". "7 active days
 *     in a row" looked exactly like "idle is 33%".
 *   · the after-hours signal printed the raw window timezone ("UTC time") at a
 *     reader who has no idea what the org's configured zone is.
 *
 * Groups, in reading order:
 *   attention  something is wrong or heading that way — act
 *   watch      true and worth knowing, not yet a problem
 *   good       worth saying out loud; teams under-notice these
 *   context    explains the data itself, not the person
 */

/* ── group vocabulary ────────────────────────────────────────────────────── */
const GROUPS = {
  attention: { label: "Needs attention", icon: AlertTriangle, tone: "negative" },
  watch: { label: "Worth watching", icon: Eye, tone: "warning" },
  good: { label: "Going well", icon: CheckCircle2, tone: "positive" },
  context: { label: "About this data", icon: Info, tone: "info" },
};

const pct = (v) => `${Math.round((Number(v) || 0) * 100)}%`;

/**
 * kind → { group, title, meaning }
 *
 * `title` reframes the measurement as a statement about the person's week.
 * `meaning` is the "so what" — omitted where the title already carries it,
 * because a filler sentence under every row trains the reader to skip them.
 *
 * `s` is the server signal, `ctx` the dossier metrics.
 */
const FRAMING = {
  silence: () => ({
    group: "attention",
    title: "Nothing tracked at all this period",
    meaning: "Either tracking is not running on their machine, or they did not work. Worth one message before you assume the second.",
  }),

  idle_drag: (s, ctx) => {
    const ratio = Number(s?.evidence?.value) || 0;
    const severe = ratio >= 0.45;
    return {
      group: severe ? "attention" : "watch",
      title: severe
        ? `Nearly half their tracked time was idle (${pct(ratio)})`
        : `Idle ran at ${pct(ratio)} of tracked time`,
      meaning: severe
        ? "At this level the tracked total is not a useful measure of output. Usually it means the timer is left running through meetings, calls or breaks."
        : "Normal for a role with meetings or calls. Worth a look only if it keeps climbing.",
    };
  },

  pace_drop: (s) => {
    const drop = Math.abs(Math.round(Number(s?.evidence?.value) || 0));
    return {
      group: drop >= 40 ? "attention" : "watch",
      title: `Tracked time fell ${drop}% against the previous period`,
      meaning: "A drop this size is usually leave, a project handover, or tracking that stopped running — not a person who suddenly stopped working. Check which before reading anything into it.",
    };
  },

  pace_rise: (s) => ({
    group: "watch",
    title: `Tracked time rose ${Math.abs(Math.round(Number(s?.evidence?.value) || 0))}% against the previous period`,
    meaning: "Worth knowing why. A sustained rise with no deadline behind it is the shape overwork usually starts as.",
  }),

  after_hours: (s, ctx) => {
    const ratio = Number(s?.evidence?.value) || 0;
    const tz = ctx?.period?.timezone;
    return {
      group: ratio >= 0.35 ? "attention" : "watch",
      title: `${pct(ratio)} of their active time was outside normal hours`,
      // The server sentence ends with the raw zone name, which means nothing to
      // a reader. Name the zone as the org's setting, or leave it out.
      meaning: `Work landing late at night or early morning${tz ? `, measured in the org timezone (${tz})` : ""}. If this is not their agreed schedule, it is the earliest burnout signal you get.`,
    };
  },

  context_switching: (s) => ({
    group: "watch",
    title: `Their work was fragmented across ${s?.evidence?.value || "several"} hours`,
    meaning: "Four or more projects inside a single hour, repeatedly. Deep work is unlikely in this shape — usually a sign of too many parallel commitments rather than poor focus.",
  }),

  single_project_focus: (s) => ({
    group: "context",
    title: s?.label || "Concentrated on one project",
    meaning: "Not a problem in itself. It does mean this person is a single point of knowledge on that project.",
  }),

  goal_stall: (s, ctx, dossier) => {
    const cycle = dossier?.goals?.cycle;
    const closed = String(cycle?.status || "").toLowerCase() === "completed";
    return {
      group: closed ? "watch" : "attention",
      title: closed
        ? `${s?.evidence?.value || "Some"} goals closed the cycle with no recorded progress`
        : `${s?.evidence?.value || "Some"} goals still have no recorded progress`,
      meaning: closed
        ? "The cycle is already closed, so this is a record-keeping gap rather than a live risk — either the work was not logged, or the goals were never real."
        : "Either the work has not started or it is not being recorded against the goal. Both are worth a direct question.",
    };
  },

  streak: (s) => ({
    group: "good",
    title: `${s?.evidence?.value || "Several"} active days in a row`,
    meaning: "Consistent, not spiky. Only read it as a concern if the run has no days off in it at all.",
  }),

  low_productivity: (s) => ({
    group: "attention",
    title: `Their analysed hours score low (${s?.evidence?.value ?? "—"} of 100)`,
    meaning: "This score comes from on-screen activity density, so it under-rates reading, calls and thinking. Treat it as a prompt to look at the work log, never as a verdict.",
  }),

  no_summary_pipeline: () => ({
    group: "context",
    title: "No written analysis was generated for this window",
    meaning: "The figures here come from activity density alone. The work log will be thin — that is a pipeline gap, not an idle person.",
  }),
};

/**
 * dossier → grouped, manager-facing signals.
 * Unknown kinds are kept rather than dropped, with the server's own wording:
 * a new signal type appearing should degrade to "still shown", never vanish.
 */
export function interpretSignals(dossier) {
  const list = Array.isArray(dossier?.signals) ? dossier.signals : [];
  const ctx = dossier?.metrics;

  const out = list.map((s) => {
    const frame = FRAMING[s.kind || s.id];
    if (frame) {
      const f = frame(s, ctx, dossier) || {};
      return {
        key: s.id || s.kind,
        group: f.group || "watch",
        title: f.title || s.label || "Signal",
        meaning: f.meaning || "",
        evidence: s.detail || "",
      };
    }
    // Unknown kind → severity is the only guide we have.
    const sev = String(s.severity || "").toLowerCase();
    return {
      key: s.id || s.kind || s.label,
      group: sev === "critical" || sev === "high" ? "attention"
        : sev === "positive" ? "good"
          : sev === "info" ? "context" : "watch",
      title: s.label || "Signal",
      meaning: "",
      evidence: s.detail || "",
    };
  });

  const order = ["attention", "watch", "good", "context"];
  return order
    .map((g) => ({ group: g, ...GROUPS[g], items: out.filter((x) => x.group === g) }))
    .filter((g) => g.items.length > 0);
}

/* ── render ──────────────────────────────────────────────────────────────── */

export function SignalList({ dossier, onPick }) {
  const t = useDashTheme();
  const groups = React.useMemo(() => interpretSignals(dossier), [dossier]);
  if (!groups.length) return null;

  const toneColor = (tone) => ({
    negative: t.status.negative,
    warning: t.status.warning,
    positive: t.status.positive,
    info: t.status.info,
  }[tone] || t.status.info);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {groups.map((g) => {
        const Icon = g.icon;
        const color = toneColor(g.tone);
        return (
          <div key={g.group}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
              fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color,
            }}>
              <Icon size={12} /> {g.label}
              <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>· {g.items.length}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {g.items.map((it) => {
                const Tag = onPick ? "button" : "div";
                return (
                  <Tag
                    key={it.key}
                    onClick={onPick ? () => onPick(it) : undefined}
                    style={{
                      display: "flex", gap: 10, padding: "10px 12px", borderRadius: t.r.md,
                      background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
                      border: "none", textAlign: "left", width: "100%",
                      cursor: onPick ? "pointer" : "default",
                      fontFamily: t.font.family, color: t.c.text,
                    }}
                  >
                    <span style={{ width: 3, borderRadius: 2, flexShrink: 0, background: color }} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                        {it.title}
                      </span>
                      {it.meaning && (
                        <span style={{ display: "block", fontSize: 12, color: t.c.textSecondary, lineHeight: 1.55, marginTop: 4 }}>
                          {it.meaning}
                        </span>
                      )}
                      {it.evidence && (
                        <span style={{
                          display: "block", fontSize: 11.5, color: t.c.textTertiary,
                          lineHeight: 1.5, marginTop: 5, fontVariantNumeric: "tabular-nums",
                        }}>
                          {it.evidence}
                        </span>
                      )}
                    </span>
                  </Tag>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default SignalList;
